import { useEffect, useState, useRef } from 'react'
import { getAllProducts, createProduct, updateProduct, deleteProduct } from '../api'
import { invalidateProductCache } from '../components/invoice/InvoiceForm'
import ProcessingOverlay from '../components/ui/ProcessingOverlay'

const empty = { name: '', unit: 'PCS', price: '', dept_code: 'SIL' }
const DEPARTMENTS = [{ code: 'SIL', label: 'Laptop' }, { code: 'SIH', label: 'Mobile' }]
const PAGE_SIZE = 50

export default function Products() {
  const [allProducts, setAllProducts] = useState<any[]>([])
  const [form, setForm] = useState(empty)
  const [editId, setEditId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [deptFilter, setDeptFilter] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [processing, setProcessing] = useState('')
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  const nameRef = useRef<HTMLInputElement>(null)
  const deptRef = useRef<HTMLSelectElement>(null)
  const unitRef = useRef<HTMLInputElement>(null)
  const priceRef = useRef<HTMLInputElement>(null)

  const load = () => {
    setLoading(true)
    getAllProducts().then((r: any) => {
      setAllProducts(r.data)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const filtered = allProducts
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.dept_code || '').toLowerCase().includes(search.toLowerCase()))
    .filter(p => !deptFilter || p.dept_code === deptFilter)
    .sort((a, b) => b.id - a.id)

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [search])

  const handleSubmit = async () => {
    if (!form.name.trim()) return
    setProcessing(editId ? 'Updating product...' : 'Saving product...')
    const payload = { ...form, price: parseFloat(form.price) || 0, dept_code: form.dept_code || null }
    try {
      if (editId) { await updateProduct(editId, payload) }
      else { await createProduct(payload) }
      invalidateProductCache()
      setForm(empty); setEditId(null); setShowForm(false); load()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Save failed')
    } finally { setProcessing('') }
  }

  const handleFormKeyDown = (e: React.KeyboardEvent, fieldIdx: number) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSubmit()
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const refs = [nameRef, deptRef, unitRef, priceRef]
      if (fieldIdx < refs.length - 1) {
        refs[fieldIdx + 1].current?.focus()
      }
    }
  }

  const startEdit = (p: any) => {
    setEditId(p.id)
    setForm({ name: p.name, unit: p.unit, price: String(p.price), dept_code: p.dept_code || '' })
    setShowForm(true)
  }

  return (
    <div className="page">
      {processing && <ProcessingOverlay message={processing} />}
      <div className="page-header">
        <div>
          <div className="page-title">Products / Barang</div>
          <div className="page-subtitle">Manage product master data ({allProducts.length} total)</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); setTimeout(() => nameRef.current?.focus(), 100) }}>
          + Add Product
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 15 }}>{editId ? 'Edit Product' : 'New Product'}</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>Enter moves to next field · Ctrl+Enter saves</div>
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Product Name *</label>
              <input className="form-input" ref={nameRef} value={form.name} required
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                onKeyDown={e => handleFormKeyDown(e, 0)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Department *</label>
              <select className="form-input" ref={deptRef} value={form.dept_code}
                onChange={e => setForm(f => ({ ...f, dept_code: e.target.value }))}
                onKeyDown={e => handleFormKeyDown(e, 1)}>
                {DEPARTMENTS.map(d => <option key={d.code} value={d.code}>{d.label} ({d.code})</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Unit</label>
              <input className="form-input" ref={unitRef} value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                onKeyDown={e => handleFormKeyDown(e, 2)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Price</label>
              <input type="number" className="form-input" ref={priceRef} value={form.price} min="0" step="0.01"
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                onKeyDown={e => handleFormKeyDown(e, 3)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-primary" onClick={handleSubmit}>{editId ? 'Update' : 'Save'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); setForm(empty) }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'end' }}>
          <div className="search-bar" style={{ flex: 1 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-input" style={{ width: 160 }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">All Departments</option>
            <option value="SIL">Laptop (SIL)</option>
            <option value="SIH">Mobile (SIH)</option>
          </select>
          <button className="btn btn-primary" style={{ height: 38 }} onClick={load}>🔍 Search</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 48 }}>Loading products...</div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Dept</th>
                  <th>Unit</th>
                  <th className="right">Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No products found</td></tr>
                )}
                {paginated.map((p: any) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>
                      {p.dept_code
                        ? <span className="badge badge-draft">{p.dept_code}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>
                      }
                    </td>
                    <td style={{ color: '#64748b' }}>{p.unit}</td>
                    <td className="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number(p.price) > 0
                        ? Number(p.price).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        : <span style={{ color: '#cbd5e1' }}>—</span>
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => startEdit(p)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={async () => {
                          if (window.confirm('Delete product?')) {
                            setProcessing('Deleting product...')
                            try { await deleteProduct(p.id); invalidateProductCache(); load() } finally { setProcessing('') }
                          }
                        }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '8px 0' }}>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span style={{ padding: '4px 12px', fontSize: 13, color: '#64748b' }}>Page {page} / {totalPages}</span>
                  <button className="btn btn-sm btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
